import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { publicService } from "@/services/publicService";
import { adminService } from "@/services/portalServices";
import { qk } from "@/api/queryKeys";
import { useAuth } from "@/features/auth/AuthProvider";
import {
  UserSquare2,
  Users2,
  Newspaper,
  Calendar,
  Radio,
  Image as ImageIcon,
  ShieldCheck,
  LayoutDashboard,
  Trophy,
} from "lucide-react";

/**
 * Frontend global search backed by the configured API service
 * layer. When the PHP backend adds GET /public/search and GET /admin/search,
 * point the queries below at those endpoints instead — the shape of the
 * results stays the same.
 */

// ------- global open/close store (no context needed) ---------------------
let openState = false;
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const getSnapshot = () => openState;
const setOpen = (v: boolean) => {
  openState = v;
  listeners.forEach((l) => l());
};

export function useGlobalSearch() {
  const isOpen = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    isOpen,
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!openState),
  };
}

export function GlobalSearch() {
  const { isOpen, close } = useGlobalSearch();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!openState);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const players = useQuery({
    queryKey: qk.public.players({ perPage: 30 }),
    queryFn: () => publicService.players({ perPage: 30 }),
    enabled: isOpen,
  });
  const teams = useQuery({
    queryKey: qk.public.teams,
    queryFn: publicService.teams,
    enabled: isOpen,
  });
  const articles = useQuery({
    queryKey: qk.public.articles({ perPage: 20 }),
    queryFn: () => publicService.articles({ perPage: 20 }),
    enabled: isOpen,
  });
  const fixtures = useQuery({
    queryKey: qk.public.fixtures({ perPage: 20 }),
    queryFn: () => publicService.fixtures({ perPage: 20 }),
    enabled: isOpen,
  });
  const streams = useQuery({
    queryKey: qk.public.streams({ perPage: 10 }),
    queryFn: () => publicService.streams({ perPage: 10 }),
    enabled: isOpen,
  });
  const galleries = useQuery({
    queryKey: qk.public.galleries({ perPage: 10 }),
    queryFn: () => publicService.galleries({ perPage: 10 }),
    enabled: isOpen,
  });
  const sponsors = useQuery({
    queryKey: qk.public.sponsors,
    queryFn: publicService.sponsors,
    enabled: isOpen,
  });
  const isAdmin =
    !!user &&
    user.roles.some(
      (r) => r.includes("administrator") || r === "content_editor" || r === "media_manager",
    );
  const adminUsers = useQuery({
    queryKey: qk.admin.users({ perPage: 30 }),
    queryFn: () => adminService.users({ perPage: 30 }),
    enabled: isOpen && isAdmin,
  });

  const go = useCallback(
    (to: string) => {
      close();
      navigate(to);
    },
    [close, navigate],
  );

  const shortcuts = useMemo(
    () => [
      { label: "Home", to: "/", icon: LayoutDashboard },
      { label: "Fixtures", to: "/fixtures", icon: Calendar },
      { label: "Results", to: "/results", icon: Trophy },
      { label: "League table", to: "/league-tables", icon: Trophy },
      { label: "Watch Live", to: "/watch", icon: Radio },
      { label: "News", to: "/news", icon: Newspaper },
      { label: "Galleries", to: "/galleries", icon: ImageIcon },
      { label: "Contact", to: "/contact", icon: Newspaper },
      ...(isAdmin
        ? [
            { label: "Admin Dashboard", to: "/admin", icon: LayoutDashboard },
            { label: "Admin Users", to: "/admin/users", icon: ShieldCheck },
            { label: "Admin Players", to: "/admin/players", icon: UserSquare2 },
            { label: "Admin Articles", to: "/admin/articles", icon: Newspaper },
            { label: "Admin Streams", to: "/admin/streams", icon: Radio },
          ]
        : []),
    ],
    [isAdmin],
  );

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandInput placeholder="Search players, teams, news, fixtures…" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>

        <CommandGroup heading="Go to">
          {shortcuts.map((s) => (
            <CommandItem key={s.to} onSelect={() => go(s.to)}>
              <s.icon className="mr-2 size-4 text-gold-dark" /> {s.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />

        {players.data ? (
          <CommandGroup heading="Players">
            {players.data.data.slice(0, 12).map((p) => (
              <CommandItem key={p.publicId} onSelect={() => go(`/players/${p.slug ?? p.publicId}`)}>
                <UserSquare2 className="mr-2 size-4 text-gold-dark" /> #{p.shirtNumber} ·{" "}
                {p.displayName}
                <span className="ml-auto text-xs text-muted-foreground">{p.position}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {teams.data ? (
          <CommandGroup heading="Teams">
            {teams.data.map((t) => (
              <CommandItem key={t.publicId} onSelect={() => go(`/teams/${t.slug}`)}>
                <Users2 className="mr-2 size-4 text-gold-dark" /> {t.name}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {fixtures.data ? (
          <CommandGroup heading="Fixtures">
            {fixtures.data.data.slice(0, 8).map((f) => (
              <CommandItem key={f.publicId} onSelect={() => go(`/matches/${f.publicId}`)}>
                <Calendar className="mr-2 size-4 text-gold-dark" /> {f.homeTeam.name} vs{" "}
                {f.awayTeam.name}
                <span className="ml-auto text-xs text-muted-foreground">{f.competition}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {articles.data ? (
          <CommandGroup heading="News">
            {articles.data.data.slice(0, 8).map((a) => (
              <CommandItem key={a.publicId} onSelect={() => go(`/news/${a.slug}`)}>
                <Newspaper className="mr-2 size-4 text-gold-dark" /> {a.title}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {streams.data ? (
          <CommandGroup heading="Streams">
            {streams.data.data.map((s) => (
              <CommandItem key={s.publicId} onSelect={() => go(`/watch/${s.publicId}`)}>
                <Radio className="mr-2 size-4 text-gold-dark" /> {s.title}
                <span className="ml-auto text-xs text-muted-foreground">
                  {s.status.replace(/_/g, " ")}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {galleries.data ? (
          <CommandGroup heading="Galleries">
            {galleries.data.data.slice(0, 6).map((g) => (
              <CommandItem key={g.publicId} onSelect={() => go(`/galleries/${g.publicId}`)}>
                <ImageIcon className="mr-2 size-4 text-gold-dark" /> {g.title}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {sponsors.data ? (
          <CommandGroup heading="Sponsors">
            {sponsors.data.map((s) => (
              <CommandItem key={s.publicId} onSelect={() => go("/sponsors")}>
                <ShieldCheck className="mr-2 size-4 text-gold-dark" /> {s.name}
                <span className="ml-auto text-xs text-muted-foreground">{s.tier}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {isAdmin && adminUsers.data ? (
          <CommandGroup heading="Admin — Users">
            {adminUsers.data.data
              .slice(0, 8)
              .map((u: { publicId: string; name: string; email: string }) => (
                <CommandItem key={u.publicId} onSelect={() => go(`/admin/users`)}>
                  <ShieldCheck className="mr-2 size-4 text-gold-dark" /> {u.name}
                  <span className="ml-auto text-xs text-muted-foreground">{u.email}</span>
                </CommandItem>
              ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
